/**
 * useAuth — zustand-backed auth state.
 *
 * Responsibilities:
 *   · Persist access + refresh tokens to localStorage.
 *   · Decode JWT claims on every token write so the UI can read `role`,
 *     `tenant_id`, `exp` without re-decoding.
 *   · Expose imperative login/logout/setTokens calls.
 *   · Plug into @cardioq/api-client's TokenStore interface so the axios
 *     interceptor reads the same source of truth.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { JWTClaims, UserRole } from '@cardioq/engine-types';
import type { TokenStore } from '@cardioq/api-client';
import { decodeJwt, isJwtExpired } from '@/lib/jwt';

const STORAGE_KEY = 'cardioq-patient-auth';

/**
 * Display snapshot from the login response (LoginResponse carries
 * user_id/email/name/role alongside the tokens). The JWT stays the
 * authoritative source for `role`; this exists so shells can show the
 * signed-in user's name without an extra profile fetch (the insurance
 * section's AppShell-derived header reads it).
 */
export interface UserSnapshot {
  user_id?: string;
  email?: string;
  name?: string;
  role?: UserRole;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  claims: JWTClaims | null;
  user: UserSnapshot | null;
  // Persisted-method tracking — the Login screen reads this on mount
  // to default the active tab to the patient's last-used method.
  lastLoginMethod: 'mobile' | 'email' | null;
  setTokens: (access: string, refresh?: string) => void;
  setUser: (u: UserSnapshot | null) => void;
  clear: () => void;
  setLastLoginMethod: (m: 'mobile' | 'email') => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      claims: null,
      user: null,
      lastLoginMethod: null,

      setTokens: (access, refresh) => {
        const claims = decodeJwt(access);
        set({
          accessToken: access,
          refreshToken: refresh ?? null,
          claims,
        });
      },

      setUser: (u) => set({ user: u }),

      clear: () => {
        set({
          accessToken: null,
          refreshToken: null,
          claims: null,
          user: null,
        });
      },

      setLastLoginMethod: (m) => set({ lastLoginMethod: m }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        claims: s.claims,
        user: s.user,
        lastLoginMethod: s.lastLoginMethod,
      }),
      // Re-derive claims from the stored token on rehydrate so a
      // tampered-with localStorage entry can't masquerade as a valid one.
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          state.claims = decodeJwt(state.accessToken);
        }
      },
    },
  ),
);

/**
 * Hook — selector wrapper that exposes the most common derived booleans.
 * Components needing imperative actions can call `useAuthStore.getState()`
 * or pull `setTokens` / `clear` directly.
 */
export function useAuth(): {
  isAuthenticated: boolean;
  isExpired: boolean;
  role: UserRole | null;
  tenantId: string | null;
  subjectId: string | null;
  user: UserSnapshot | null;
  lastLoginMethod: 'mobile' | 'email' | null;
  login: (access: string, refresh?: string) => void;
  logout: () => void;
} {
  const state = useAuthStore();
  const claims = state.claims;
  const expired = isJwtExpired(claims);

  return {
    isAuthenticated: Boolean(state.accessToken && claims),
    isExpired: expired,
    role: claims?.role ?? null,
    tenantId: claims?.tenant_id ?? null,
    subjectId: claims?.sub ?? null,
    user: state.user,
    lastLoginMethod: state.lastLoginMethod,
    login: state.setTokens,
    logout: state.clear,
  };
}

/**
 * TokenStore adapter — passed to `configureApiClient` so the axios
 * interceptor reads from the same zustand store. Keeps a single source
 * of truth for the bearer token.
 */
export const authTokenStore: TokenStore = {
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  setTokens: (access, refresh) => useAuthStore.getState().setTokens(access, refresh),
  clear: () => useAuthStore.getState().clear(),
};
